import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, ChevronLeft, ChevronRight, ExternalLink, Link as LinkIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { generateProjectSlug } from '@/lib/slug';

interface Project {
  title: string;
  description: string;
  screens: string[];
  link?: string;
}

interface ProjectDetailProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ProjectDetail = ({ project, isOpen, onClose }: ProjectDetailProps) => {
  const { t } = useTranslation();
  const [currentImage, setCurrentImage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');

  if (!project) return null;

  const nextImage = () => setCurrentImage((prev) => (prev + 1) % project.screens.length);
  const prevImage = () => setCurrentImage((prev) => (prev - 1 + project.screens.length) % project.screens.length);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      setCurrentImage(0);
    }
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/projects/${generateProjectSlug(project.title)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
    setTimeout(() => setCopyState('idle'), 1600);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl w-full max-h-screen overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl md:text-3xl">{project.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Image Carousel */}
          <div className="relative bg-muted rounded-lg overflow-hidden">
            <div className="relative aspect-[9/16] sm:aspect-[4/5] md:aspect-video bg-muted flex items-center justify-center">
              {isLoading && <Skeleton className="absolute inset-0 w-full h-full rounded-none" />}
              <img
                src={project.screens[currentImage]}
                alt={`${project.title} screenshot ${currentImage + 1}`}
                className={`w-full h-full object-contain ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity`}
                onLoad={() => setIsLoading(false)}
                onError={(e) => {
                  setIsLoading(false);
                  e.currentTarget.src =
                    'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&h=600&fit=crop';
                }}
              />
            </div>

            {/* Navigation Buttons */}
            {project.screens.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-card/80 backdrop-blur-sm p-2 rounded-full hover:bg-card transition-colors z-10"
                  aria-label={t('projectDetail.previousImageAria')}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-card/80 backdrop-blur-sm p-2 rounded-full hover:bg-card transition-colors z-10"
                  aria-label={t('projectDetail.nextImageAria')}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>

                {/* Image Indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 flex-wrap justify-center">
                  {project.screens.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setCurrentImage(idx);
                        setIsLoading(true);
                      }}
                      className={`h-2 rounded-full transition-all ${
                        idx === currentImage ? 'w-6 bg-primary' : 'w-2 bg-card/60'
                      }`}
                      aria-label={t('projectDetail.goToImageAria', { index: idx + 1 })}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Image Counter */}
            <div className="absolute top-4 right-4 bg-card/80 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
              {currentImage + 1} / {project.screens.length}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">{t('projectDetail.aboutThisProject')}</h3>
              <p className="text-muted-foreground leading-relaxed">{project.description}</p>
            </div>

            {/* Link + Share Buttons */}
            <div className="flex flex-wrap gap-3">
              {project.link && (
                <Button
                  className="flex items-center gap-2 bg-gradient-primary hover:scale-[1.02] transition-all"
                  onClick={() => window.open(project.link, '_blank')}
                >
                  <ExternalLink className="h-4 w-4" />
                  {t('projectDetail.viewProject')}
                </Button>
              )}
              <Button variant="outline" className="flex items-center gap-2" onClick={handleCopyLink}>
                {copyState === 'copied' ? <Check className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
                {copyState === 'copied' ? t('projectDetail.copied') : copyState === 'error' ? t('projectDetail.copyFailed') : t('projectDetail.copyLink')}
              </Button>
            </div>

            {/* Technical Details */}
            <div className="text-sm text-muted-foreground">
              <p>{t('projectDetail.screenshots', { count: project.screens.length })}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
